/**
 * @fileoverview Main content router component
 *
 * Renders the appropriate view based on the current navigation state.
 * Handles authentication gates, admin routes, and account approval status.
 *
 * @module components/AppContent
 */

import React from 'react';
import { ApiUser } from '../types/api';
import {
  ViewMode,
  Accident,
  Zone,
  UserSettings,
  ReplacementVehicleRequest,
  WazeDataState,
  RadiusConfig,
  PostcodeConfig,
  PolygonConfig,
} from '../types';
import { Profile } from '../lib/database.types';
import { ListView } from './ListView';
import { MapView } from './MapView';
import { SettingsView } from './SettingsView';
import { ZonesView } from './ZonesView';
import { AddZoneView } from './AddZoneView';
import { RequestsView } from './RequestsView';
import { ReplacementRequestScreen } from './ReplacementRequestScreen';
import { ReplacementLoadingScreen } from './ReplacementLoadingScreen';
import { ReplacementTrackingScreen } from './ReplacementTrackingScreen';
import { AdminDashboard } from './admin/AdminDashboard';
import { Loader } from './Loader';
import { SignInPage } from './SignInPage';
import { PendingApprovalScreen } from './PendingApprovalScreen';
import { getZonesBoundingBox } from '../services/zoneService';

/**
 * Props for the AppContent component
 */
interface AppContentProps {
  /** Current view/route to display */
  currentView: ViewMode['current'];
  user: ApiUser | null;
  /** User's profile from database */
  profile: Profile | null;
  /** Whether authentication is still loading */
  authLoading: boolean;
  /** Whether profile is still loading */
  profileLoading: boolean;
  /** Callback when sign-in is completed */
  onSignIn: () => void;
  /** Callback to sign out the user */
  onSignOut: () => void;
  /** Whether current route is an admin route */
  isAdminRoute: boolean;

  // --- Data Props ---

  /** Filtered and sorted list of accidents to display */
  sortedAccidents: Accident[];
  /** Currently selected accident (for map view) */
  selectedAccident: Accident | null;
  /** User's geographic zones from database */
  dbZones: Zone[];
  /** User's settings and preferences */
  userSettings: UserSettings;
  /** Waze data fetch state (loading, error, etc.) */
  wazeDataState: WazeDataState;
  /** List of replacement vehicle requests */
  replacementRequests: ReplacementVehicleRequest[];

  // --- Replacement Flow State ---

  /** Accident for current replacement request in progress */
  currentReplacementAccident: Accident | null;
  /** Lead name from replacement form */
  currentLeadName: string;
  /** Request being tracked on the tracking screen */
  currentTrackingRequest: ReplacementVehicleRequest | null;

  // --- Action Callbacks ---

  /** Navigate to a different view */
  setCurrentView: (view: ViewMode['current']) => void;
  /** View an accident on the map */
  onViewOnMap: (accident: Accident) => void;
  /** Start a replacement request for an accident */
  onReplacementRequest: (accident: Accident) => void;
  /** Update user settings */
  onSettingsChange: React.Dispatch<React.SetStateAction<UserSettings>>;
  /** Refresh Waze data */
  onRefresh: () => void;
  /** Toggle a zone's enabled state */
  onToggleZone: (id: string) => void;
  /** Delete a zone */
  onDeleteZone: (id: string) => void;
  /** Add a new zone */
  onAddZone: (
    name: string,
    type: 'radius' | 'postcodes' | 'polygon',
    config: RadiusConfig | PostcodeConfig | PolygonConfig
  ) => void;
  /** Submit replacement request form data */
  onReplacementSubmit: (data: { name: string; email: string; phone: string }) => void;
  /** Complete the replacement request flow */
  onReplacementComplete: () => void;
  /** View tracking for a specific request */
  onViewTracking: (request: ReplacementVehicleRequest) => void;
}

/**
 * Main content router component
 *
 * This component acts as a view router, rendering the appropriate
 * screen based on the current navigation state. It also handles:
 * - Loading states during auth/profile fetch
 * - Admin route authorization
 * - Account pending approval state
 *
 * @example
 * ```tsx
 * <AppContent
 *   currentView="list"
 *   user={user}
 *   profile={profile}
 *   // ... other props
 * />
 * ```
 */
export const AppContent: React.FC<AppContentProps> = ({
  currentView,
  user,
  profile,
  authLoading,
  profileLoading,
  onSignIn,
  onSignOut,
  isAdminRoute,
  sortedAccidents,
  selectedAccident,
  dbZones,
  userSettings,
  wazeDataState,
  replacementRequests,
  currentReplacementAccident,
  currentLeadName,
  currentTrackingRequest,
  setCurrentView,
  onViewOnMap,
  onReplacementRequest,
  onSettingsChange,
  onRefresh,
  onToggleZone,
  onDeleteZone,
  onAddZone,
  onReplacementSubmit,
  onReplacementComplete,
  onViewTracking,
}) => {
  // ---------------------------------------------------------------------------
  // LOADING STATE
  // Show spinner while checking authentication or loading profile
  // ---------------------------------------------------------------------------
  if (authLoading || (user && profileLoading)) {
    return (
      <div
        className="app-container"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Loader size="large" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // NOT AUTHENTICATED
  // Redirect to sign-in page
  // ---------------------------------------------------------------------------
  if (!user) {
    return <SignInPage onSignIn={onSignIn} />;
  }

  // ---------------------------------------------------------------------------
  // ADMIN ROUTE HANDLING
  // Check if user has admin privileges before showing admin dashboard
  // ---------------------------------------------------------------------------
  if (isAdminRoute) {
    if (profile?.is_admin) {
      return <AdminDashboard />;
    } else {
      // Access denied - show error and redirect option
      return (
        <div
          className="app-container"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '48px' }}>🔒</div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#111827', margin: 0 }}>
            Access Denied
          </h1>
          <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>
            You don't have permission to access the admin area.
          </p>
          <button
            onClick={() => (window.location.href = '/')}
            style={{
              marginTop: '16px',
              background: '#111827',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Go to App
          </button>
        </div>
      );
    }
  }

  // ---------------------------------------------------------------------------
  // PENDING APPROVAL
  // Show pending screen if user account is not yet approved
  // ---------------------------------------------------------------------------
  if (profile && profile.account_status !== 'active') {
    return <PendingApprovalScreen />;
  }

  // ---------------------------------------------------------------------------
  // VIEW ROUTING
  // Render the appropriate component based on currentView
  // ---------------------------------------------------------------------------
  switch (currentView) {
    case 'list':
      return (
        <ListView
          accidents={sortedAccidents}
          onViewOnMap={onViewOnMap}
          onReplacementRequest={onReplacementRequest}
          userSettings={userSettings}
          onSettingsChange={onSettingsChange}
          wazeDataState={wazeDataState}
          onRefresh={onRefresh}
        />
      );

    case 'map': {
      // Build list of enabled data sources for the map filter
      const enabledSources = Object.entries(userSettings.dataSources)
        .filter(([, enabled]) => enabled)
        .map(([source]) => (source === 'transportNsw' ? 'transport-nsw' : source));

      // Get bounding box from zones for map initial view
      const zoneBounds = getZonesBoundingBox(dbZones);

      return (
        <MapView
          accidents={sortedAccidents}
          selectedAccident={selectedAccident}
          enabledSources={enabledSources}
          zoneBounds={zoneBounds}
        />
      );
    }

    case 'settings':
      return (
        <SettingsView
          settings={userSettings}
          onSettingsChange={onSettingsChange}
          onSignOut={onSignOut}
          isAdmin={profile?.is_admin}
          onAdminClick={() => (window.location.href = '/admin')}
        />
      );

    case 'zones':
      return (
        <ZonesView
          zones={dbZones}
          onToggleZone={onToggleZone}
          onDeleteZone={onDeleteZone}
          onAddZone={() => setCurrentView('add-zone')}
          onBack={() => setCurrentView('list')}
        />
      );

    case 'add-zone':
      return (
        <AddZoneView
          onSave={onAddZone}
          onBack={() => (dbZones.length === 0 ? setCurrentView('welcome') : setCurrentView('zones'))}
          isFirstTime={dbZones.length === 0}
        />
      );

    case 'requests':
      return <RequestsView requests={replacementRequests} onViewTracking={onViewTracking} />;

    case 'replacement-request':
      return currentReplacementAccident ? (
        <ReplacementRequestScreen
          accident={currentReplacementAccident}
          onBack={() => setCurrentView('list')}
          onSubmit={onReplacementSubmit}
        />
      ) : (
        // Fallback if no accident selected
        <ListView
          accidents={sortedAccidents}
          onViewOnMap={onViewOnMap}
          onReplacementRequest={onReplacementRequest}
          userSettings={userSettings}
          onSettingsChange={onSettingsChange}
          wazeDataState={wazeDataState}
          onRefresh={onRefresh}
        />
      );

    case 'replacement-loading':
      return currentReplacementAccident ? (
        <ReplacementLoadingScreen
          accident={currentReplacementAccident}
          leadName={currentLeadName}
          onRequestComplete={onReplacementComplete}
        />
      ) : (
        // Fallback if no accident selected
        <ListView
          accidents={sortedAccidents}
          onViewOnMap={onViewOnMap}
          onReplacementRequest={onReplacementRequest}
          userSettings={userSettings}
          onSettingsChange={onSettingsChange}
          wazeDataState={wazeDataState}
          onRefresh={onRefresh}
        />
      );

    case 'replacement-tracking':
      return currentTrackingRequest ? (
        <ReplacementTrackingScreen
          request={currentTrackingRequest}
          accident={currentTrackingRequest.accident}
          onBack={() => setCurrentView('requests')}
        />
      ) : (
        // Fallback if no request selected
        <RequestsView requests={replacementRequests} onViewTracking={onViewTracking} />
      );

    default:
      // Default fallback to list view
      return (
        <ListView
          accidents={sortedAccidents}
          onViewOnMap={onViewOnMap}
          onReplacementRequest={onReplacementRequest}
          userSettings={userSettings}
          onSettingsChange={onSettingsChange}
          wazeDataState={wazeDataState}
          onRefresh={onRefresh}
        />
      );
  }
};
